import { ApolloQueryResult, useApolloClient } from '@apollo/client';
import clsx from 'clsx';
import React, { useState } from 'react';
import { DropdownItemType } from 'types/common';

import Button from '@/components/global/Button';
import Dropdown from '@/components/global/Dropdown';
import { PanelGlyphs } from '@/components/global/PanelFont/PanelGlyphs';
import { FontStyle, FontStyleVersion } from '@/components/global/PanelFont/types';
import { Spinner } from '@/components/Spinner';
import { getProductFontSizeAdjust, isFamilyVariant, isVariableFont } from '@/lib/productHelpers';
import { DEFAULT_LOCALE, localeToEnum } from '@/lib/regions';
import { formatAwsS3FontData, generateAwsFontDataPromise, getVersionScripts } from '@/lib/util';
import { ProductByIdDocument, ProductByIdQuery, ProductByIdQueryVariables } from '@/saleor/api';

import styles from './ColophonStandard.module.scss';

interface FontStyles {
  name: any;
  versions: FontStyleVersion[];
}

const ColophonStandard = ({
  title,
  description,
  languages = [],
  products,
  serverFontFamilyData
}) => {
  const apolloClient = useApolloClient();
  const [fontStylesData, setFontStylesData] = useState({});
  const [fontFamily, setFontFamily] = useState(null);
  const [fontStyles, setFontStyles] = useState<FontStyles[]>(null);
  const [fontStyleActive, setFontStyleActive] = useState<FontStyle>(null);
  const [fontStyleVersionActive, setFontStyleVersionActive] = useState<FontStyleVersion>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fontStylesDataList = { ...serverFontFamilyData, ...fontStylesData };

  const selectedFontFamily = fontFamily || products[0];
  const fontStylesList = fontStyles || fontStylesDataList[selectedFontFamily.slug];
  const selectedFontStyle = fontStyleActive || fontStylesList?.[0];
  const versionsList = selectedFontStyle?.versions;
  const selectedVersion =
    fontStyleVersionActive || versionsList?.find(({ version }) => version === 'std');
  const fontStyleVersionScripts = getVersionScripts(selectedVersion);

  const [fontStyleVersionScriptActive, setFontStyleVersionScriptActive] = useState<string | null>(
    fontStyleVersionScripts?.[0] || null
  );

  // Computed variables
  const fontStyleVersionChars = fontStyleVersionScriptActive
    ? selectedVersion.fondue.categorisedCharacters.filter((char) => {
        if (char.script) {
          return char.script === fontStyleVersionScriptActive;
        }

        // Default: Show glyphs with null script data when the script filter is Latin
        return char.script === null && fontStyleVersionScriptActive === 'latin';
      })
    : selectedVersion?.fondue?.categorisedCharacters;
  const fontSizeAdjust = getProductFontSizeAdjust(selectedFontFamily);

  // Get font data here
  const getFontStylesData = async (fontFamily) => {
    setIsLoading(true);

    if (fontFamily && fontStylesDataList[fontFamily.slug]) {
      const data = fontStylesDataList[fontFamily.slug];
      setFontStyles(data);
      setFontStyleActive(data[0]);
      setFontStyleVersionActive(data[0].versions?.find(({ version }) => version === 'std'));
      setFontStyleVersionScriptActive(
        getVersionScripts(data[0].versions?.find(({ version }) => version === 'std'))?.[0]
      );

      setIsLoading(false);
      return;
    }

    const productRes: ApolloQueryResult<ProductByIdQuery> = await apolloClient.query<
      ProductByIdQuery,
      ProductByIdQueryVariables
    >({
      query: ProductByIdDocument,
      variables: {
        id: fontFamily.id,
        channel: process.env.NEXT_PUBLIC_DEFAULT_CHANNEL,
        locale: localeToEnum(DEFAULT_LOCALE)
      }
    });

    const productFontFamily = productRes.data.product;
    const variantFontStyles = productFontFamily.variants;

    // Construct promises for fetching wakamai fondue data from AWS
    const awsS3fontDataPromises = variantFontStyles
      .filter((fontStyle) => !isFamilyVariant(fontStyle))
      .filter((fontStyle) => !isVariableFont(fontStyle))
      .filter((fontStyle) => !fontStyle.sku.includes('-pro'))
      .map((fontStyle) => generateAwsFontDataPromise(fontStyle));

    try {
      const awsS3FontData = await Promise.all(awsS3fontDataPromises);

      const awsS3fontDataFormatted = formatAwsS3FontData(awsS3FontData);
      const awsS3fontDataFormattedArray = Object.values(awsS3fontDataFormatted) as FontStyle[];
      const versionActive = awsS3fontDataFormattedArray[0].versions?.find(
        ({ version }) => version === 'std'
      );

      setFontStyles(awsS3fontDataFormattedArray);
      setFontStyleActive(awsS3fontDataFormattedArray[0]);
      setFontStyleVersionActive(versionActive);
      setFontStyleVersionScriptActive(getVersionScripts(versionActive)?.[0]);

      // Store data by font family slug key so can check if been fetched if requested again
      setFontStylesData({
        ...fontStylesData,
        [`${fontFamily.slug}`]: awsS3fontDataFormattedArray
      });
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  const handleOnChangeFontFamily = (e) => {
    const fontFamilySelected = products.find((font) => font.name === e.name);

    setFontFamily(fontFamilySelected);
    setFontStyleVersionScriptActive(null);

    getFontStylesData(fontFamilySelected);

    return fontFamilySelected;
  };

  const handleOnChangeFontStyle = (e: FontStyle): FontStyle => {
    const fontStyleSelected = fontStylesList.find((font) => font.name === e.name);

    setFontStyleActive(fontStyleSelected);
    setFontStyleVersionActive(fontStyleSelected.versions?.find(({ version }) => version === 'std'));
    setFontStyleVersionScriptActive(fontStyleVersionScripts?.[0]);

    return fontStyleSelected;
  };

  const handleOnChangeFontStyleVersionScript = (script: string): void => {
    setFontStyleVersionScriptActive(script);
  };
  const handleOnChangeFontStyleVersionScriptDropdown = (item: DropdownItemType) => {
    setFontStyleVersionScriptActive(item.name);
    return item;
  };

  return (
    <div className={styles.ColophonStandard}>
      <div className={styles.ColophonStandard__header}>
        <h1 className={clsx('u-h2', styles.ColophonStandard__title)}>{title}</h1>
        <p className={clsx('u-h5', styles.ColophonStandard__description)}>{description}</p>
      </div>
      <div className={styles.ColophonStandard__content}>
        <PanelGlyphs
          fontSizeAdjust={fontSizeAdjust}
          fontStyleVersionActive={selectedVersion}
          fontStyleVersionChars={fontStyleVersionChars}
          childrenHeader={
            <>
              <div className={styles.ColophonStandard__panelHeaderLeft}>
                <Dropdown
                  defaultValue={selectedFontFamily}
                  items={products}
                  onSelect={handleOnChangeFontFamily}
                  color={'var(--color-primary)'}
                  inline
                  size="small"
                  fullWidth
                />
                {!isLoading && fontStylesList && fontStylesList.length > 1 && (
                  <Dropdown
                    defaultValue={selectedFontStyle}
                    items={fontStylesList}
                    onSelect={handleOnChangeFontStyle}
                    color={'var(--color-primary)'}
                    inline
                    size="small"
                    fullWidth
                  />
                )}
                {!isLoading && !!fontStyleVersionScripts.length && (
                  <div className={styles.ColophonStandard__ScriptsMobile}>
                    <Dropdown
                      onSelect={handleOnChangeFontStyleVersionScriptDropdown}
                      defaultValue={
                        fontStyleVersionScriptActive && {
                          name: fontStyleVersionScriptActive
                        }
                      }
                      items={fontStyleVersionScripts.map((script) => ({ name: script }))}
                      color={'var(--color-primary)'}
                      size="small"
                      fullWidth
                    />
                  </div>
                )}
              </div>
              <div className={styles.ColophonStandard__panelHeaderRight}>
                {!isLoading &&
                  fontStyleVersionScripts.map((script) => (
                    <Button
                      className={styles.ColophonStandard__scriptsButton}
                      variant="asLink"
                      key={script}
                      isActive={fontStyleVersionScriptActive === script}
                      callBack={() => {
                        handleOnChangeFontStyleVersionScript(script);
                      }}
                    >
                      {script}
                    </Button>
                  ))}
              </div>
            </>
          }
          {...(isLoading && {
            childrenContent: <Spinner theme="white" relative />
          })}
          childrenFooter={
            <div className={styles.ColophonStandard__footer}>
              <ul className={styles.languages}>
                {languages.map((language) => {
                  const str = language.split(' ');
                  const abbr = str?.[0];
                  const lang = str.slice(1).join(' ');

                  return (
                    <li key={abbr}>
                      <span className={styles.abbr}>{abbr}</span>
                      <span>{lang}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default ColophonStandard;
